import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation } from 'react-router'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useLocalStorage from '@/hooks/useLocalStorage'
import useStatefulSearchParam from '@/hooks/useStatefulSearchParam'
import { useGetSavedViews } from '@/service-library/hooks/saved-views'
import { View } from '@/types/views'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

type ViewsContextValue = ReturnType<typeof useGetSavedViews> & {
  allViews: View[]
  canEdit: boolean
  defaultViewId: string
  savedEnabled: boolean
  selectedView: View
  handleCancelChange: (onCancel: () => void) => void
  handleChange: () => void
  setSavedEnabled: Dispatch<SetStateAction<boolean>>
  setDefaultViewId: (viewId: string) => void
  setSelectedViewId: (newValue: string) => void
}

const ViewsContext = createContext<ViewsContextValue>({} as ViewsContextValue)

type ViewsProviderProps = {
  children: ReactNode
}

export const useViewsContext = () => useContext(ViewsContext)

export default function ViewsProvider({ children }: ViewsProviderProps) {
  const { pathname, state } = useLocation()
  const { project } = useProjectContext()
  const { isAuthorized: canEditProject } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )
  const [savedEnabled, setSavedEnabled] = useState(false)

  const [projDefaultSortColumn] = useLocalStorage(
    'proj-default-sort-columns',
    'enteredAtColId',
  )

  const [projDefaultSortOrder] = useLocalStorage(
    'proj-default-sort-order',
    'desc',
  )

  const [storedDefaultViewId, setStoredDefaultViewId] = useLocalStorage(
    `default-view-${project.id}`,
    'default',
  )

  const { savedViews, ...rest } = useGetSavedViews({
    filters: {
      project_id: project.id,
      limit: '100',
    },
  })

  const allViews = useMemo(
    () => [
      {
        id: 'default',
        name: 'Standard',
        state: {
          column_order: [],
          sorting: [
            {
              id: projDefaultSortColumn,
              desc: projDefaultSortOrder === 'desc',
            },
          ],
          org_ids: [],
          flag_codes: [],
          column_visibility: {},
          team_ids: [],
          no_flags: false,
          tag_ids: [],
        },
        project_id: project.id,
      },
      ...savedViews,
    ],
    [projDefaultSortColumn, projDefaultSortOrder, project.id, savedViews],
  )

  // Resolve the default view ID, falling back to the built-in 'default' if the stored view no longer exists
  const defaultViewId = useMemo(() => {
    if (
      storedDefaultViewId &&
      allViews.some(({ id }) => id === storedDefaultViewId)
    ) {
      return storedDefaultViewId
    }
    return 'default'
  }, [storedDefaultViewId, allViews])

  const {
    value: selectedViewId,
    toggleValue: setSelectedViewId,
    isInUrl,
    resetValue,
  } = useStatefulSearchParam({
    param: 'view',
    defaultValue: defaultViewId,
  })

  const selectedView = useMemo(
    () =>
      // setting default view as fallback for when a view is deleted and selectedViewId doesn't get updated immediately or when flag is off
      allViews.find(({ id }) => id === selectedViewId) || allViews[0],
    [allViews, selectedViewId],
  )

  const handleCancelChange = (onCancel: () => void) => {
    if (savedEnabled) {
      setSavedEnabled(false)
      onCancel()
    }
  }

  const handleChange = useCallback(() => {
    if (!savedEnabled && canEditProject) {
      setSavedEnabled(true)
    }
  }, [savedEnabled, canEditProject])

  useEffect(() => {
    if (!state) {
      setSavedEnabled(false)
    }
  }, [state])

  useEffect(() => {
    if (isInUrl && ['settings', 'metrics'].includes(pathname)) {
      resetValue()
    }
  })

  const setDefaultViewId = useCallback(
    (viewId: string) => {
      setStoredDefaultViewId(viewId)
    },
    [setStoredDefaultViewId],
  )

  return (
    <ViewsContext.Provider
      value={{
        allViews,
        savedViews,
        canEdit: canEditProject,
        defaultViewId,
        savedEnabled,
        selectedView,
        handleCancelChange,
        handleChange,
        setSavedEnabled,
        setDefaultViewId,
        setSelectedViewId,
        ...rest,
      }}
    >
      {children}
    </ViewsContext.Provider>
  )
}
